<template>
    <div class="file-wrapper-detail">
        <div class="file-wrapper-detail__header">
            <span>{{ $t('fileWrapperDialog.detailTitle') }}</span>
            <PtIcon icon="SolidCloseBig" class="file-wrapper-detail__header__close" @click="closePopover" />
        </div>
        <div class="file-wrapper-detail__body">
            <PtCollapse
                v-for="(item, index) in list"
                :key="item.alert_id"
                :label="item.alert_title"
                :isCollapse="index !== 0"
                class="file-wrapper-detail__container">
                <div slot="suffix" class="file-wrapper-detail__info">
                    <p :class="['file-wrapper-detail__status', { 'file-wrapper-detail__status--inactive': !item.alert_status }]" >
                        {{ item.alert_status ? $t('fileWrapperDialog.alertActive') : $t('fileWrapperDialog.alertInactive') }}
                    </p>
                    <ElPopover
                        :visible-arrow="false"
                        :open-delay="300"
                        :disabled="!item.disabled"
                        placement="bottom"
                        trigger="hover"
                    >
                        <div
                            slot="reference"
                            :class="{
                                'file-wrapper-detail__add-button': true,
                                'file-wrapper-detail__add-button--active': item.apno.includes(patent.APN),
                                'file-wrapper-detail__add-button--disable': item.disabled
                            }"
                            @click="addPatentIntoAlert(item)">
                            <PtIcon icon="SolidAddIn"/>
                            <span>{{ item.apno.includes(patent.APN) ? $t('fileWrapperDialog.added') : $t('fileWrapperDialog.addIn') }}</span>
                        </div>
                        <span>{{ $t(`fileWrapperDialog.alertDisabled${PN}`) }}</span>
                    </ElPopover>
                </div>
                <div class="file-wrapper-detail__content">
                    <!--count-->
                    <div class="file-wrapper-detail__content-item">
                        <p class="file-wrapper-detail__content-item-title">{{ $t('fileWrapperDialog.patentCount') }}</p>
                        <div class="file-wrapper-detail__content-item-body">{{ item.apno.length }}</div>
                    </div>
                    <!--type-->
                    <div class="file-wrapper-detail__content-item">
                        <p class="file-wrapper-detail__content-item-title">{{ $t('fileWrapperDialog.alertType') }}</p>
                        <div class="file-wrapper-detail__content-item-body">
                            <p class="file-wrapper-detail__content-item-alert-type" v-for="type in item.formatAlertType.US" :key="type">
                                {{ $t(`fileWrapperDialog.alertTypeMap['${type}']`) }}(US)
                            </p>
                            <p class="file-wrapper-detail__content-item-alert-type" v-for="type in item.formatAlertType.EP" :key="type">
                                {{ $t(`fileWrapperDialog.alertTypeMap['${type}']`) }}(EP)
                            </p>
                            <p class="file-wrapper-detail__content-item-alert-type" v-for="type in item.formatAlertType.CN" :key="type">
                                {{ $t(`fileWrapperDialog.alertTypeMap['${type}']`) }}(CN)
                            </p>
                        </div>
                    </div>
                    <!--Send-->
                    <div class="file-wrapper-detail__content-item">
                        <p class="file-wrapper-detail__content-item-title">{{ $t('fileWrapperDialog.sendTo') }}</p>
                        <div class="file-wrapper-detail__content-item-body">
                            <p class="file-wrapper-detail__content-item-email" v-if="item.send_to_myself">
                                {{ item.email }} ({{ $t('emailAlertDialog.self') }})
                            </p>
                            <p class="file-wrapper-detail__content-item-email" v-if="item.alert_send && item.alert_send.length">
                                {{ item.alert_send[0].name || item.alert_send[0].email }} ({{ $t('fileWrapperDialog.inTotal', { count: getTotalCount(item) }) }})
                            </p>
                        </div>
                    </div>
                    <!--Frequency-->
                    <div class="file-wrapper-detail__content-item">
                        <p class="file-wrapper-detail__content-item-title">{{ $t('fileWrapperDialog.frequency') }}</p>
                        <div class="file-wrapper-detail__content-item-body">
                            <span v-if="item.alert_frequency_type === 'W'">
                                {{ $t('fileWrapperDialog.send.W', { week: transWeek[item.alert_frequency_date - 1] }) }}
                            </span>
                            <span v-else-if="item.alert_frequency_type === 'M'">
                                {{ $t('fileWrapperDialog.send.M', { date: transDates[(item.alert_frequency_date > 28 ? 29 : item.alert_frequency_date) - 1] }) }}
                            </span>
                            <span v-else>
                                {{ $t('fileWrapperDialog.send.D') }}
                            </span>
                        </div>
                    </div>
                    <!--lastSend-->
                    <div class="file-wrapper-detail__content-item">
                        <p class="file-wrapper-detail__content-item-title">{{ $t('fileWrapperDialog.lastSend') }}</p>
                        <div class="file-wrapper-detail__content-item-body">
                            <span v-if="item.alert_send_prev_ts">{{ item.alert_send_prev_ts | formatDateNormal }}</span>
                            <span v-else>{{ $t('fileWrapperDialog.noSendPrev') }}</span>
                        </div>
                    </div>
                    <!--nextSend-->
                    <div class="file-wrapper-detail__content-item">
                        <p class="file-wrapper-detail__content-item-title">{{ $t('fileWrapperDialog.nextSend') }}</p>
                        <div class="file-wrapper-detail__content-item-body">
                            <span v-if="item.alert_send_next_ts">{{ item.alert_send_next_ts | formatDateNormal }}</span>
                            <span v-else>{{ $t('fileWrapperDialog.noSendNext') }}</span>
                        </div>
                    </div>
                </div>
            </PtCollapse>
        </div>
        <div class="file-wrapper-detail__footer">
            <div class="file-wrapper-detail__footer-button" @click="goCreate">
                <PtIcon icon="SolidAddMedium" />
                <span class="file-wrapper-detail__footer-button-text">{{ $t('fileWrapperDialog.create') }}</span>
            </div>
            <div class="file-wrapper-detail__footer-button" @click="goManage">
                <PtIcon icon="SolidSettings" />
                <span class="file-wrapper-detail__footer-button-text">{{ $t('fileWrapperDialog.manage') }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import PtCollapse from '../../../../../components/components/PtCollapse';
import { Toast, Icon } from 'patsnap-biz';
import { compact, map, cloneDeep, get, isArray, forEach } from 'lodash';
import { Popover } from 'element-ui';
import Vue from 'vue';
import moment from 'moment';
import { getI18nInstance } from '../../locales/i18nInstance';
import { getEmailAlertLimitApi, createFileWrapperApi } from 'library-@/apis/email_alert_api/email_alert';
import { CN_FILE_WRAPPER_TYPE, US_FILE_WRAPPER_TYPE, EP_FILE_WRAPPER_TYPE } from 'library-@/email-alert/field-config';
import { HOME_URI } from 'library-@/config/apiConfig';
import '@patsnap-ui/icon/assets/solid/CloseBig.svg';
import '@patsnap-ui/icon/assets/solid/AddIn.svg';
import '@patsnap-ui/icon/assets/solid/AddMedium.svg';
import '@patsnap-ui/icon/assets/solid/settings.svg';

export default {
    name: 'FileWrapperAlertDetail',
    components: {
        PtCollapse,
        PtIcon: Icon,
        ElPopover: Popover,
    },
    props: {
        alertList: {
            type: Array,
            default: () => []
        },
        patent: {
            type: Object,
            required: true,
            default: () => ({})
        },
        patentId: {
            type: String,
            require: true
        },
    },
    filters: {
        formatDateNormal(date) {
            const i18n = getI18nInstance();
            if (!date) return date;
            const dateFormat = i18n.t('fileWrapperDialog.format.date');
            if (typeof date === 'string') {
                date = date.replace(/\./g, '-');
            }
            return moment(date).format(`${dateFormat}`);
        }
    },
    data() {
        let weekdays = [];
        const dates = [];
        let emailAlertLang = this.$t('fileWrapperDialog.weekdaysArray');
        if (!emailAlertLang || !(emailAlertLang instanceof Array)) {
            emailAlertLang = [];
        }
        weekdays = map(emailAlertLang, (label, index) => {
            return { label, value: index };
        });
        let transWeek = compact(map(weekdays, 'label'));
        for (let i = 1; i <= 28; i++) {
            dates.push({
                label: this.$t('fileWrapperDialog.date', {
                    n: i,
                    fix: (function(i) {
                        switch (Vue.config.lang) {
                            case 'de':
                            case 'en':
                                if (i > 3 && i < 19) return 'th';
                                switch (i % 10) {
                                    case 1:
                                        return 'st';
                                    case 2:
                                        return 'nd';
                                    case 3:
                                        return 'rd';
                                    default:
                                        return 'th';
                                }
                            case 'cn':
                            case 'jp':
                            case 'tw':
                                return '日';
                        }
                    })(i)
                }),
                value: i
            });
        }
        // 最后一日
        dates.push({
            label: this.$t('fileWrapperDialog.lastDay'),
            value: 32
        });
        return {
            transWeek,
            transDates: map(dates, 'label'),
            isLoadingLimit: false,
            isLoadingAddIn: false,
        };
    },
    computed: {
        PN() {
            return get(this.patent, 'PN', '').substr(0, 2);
        },
        /* format alertList字段 */
        list() {
            if (!isArray(this.alertList)) return [];
            const list = cloneDeep(this.alertList);
            forEach(list, item => {
                // 将alert_type按照CN、US、EP分类
                const alertType = get(item, 'alert_type', []);
                const formatAlertType = {
                    CN: [],
                    US: [],
                    EP: []
                };
                forEach(alertType, type => {
                    if (CN_FILE_WRAPPER_TYPE.includes(type)) {
                        formatAlertType.CN.push(type);
                    } else if (US_FILE_WRAPPER_TYPE.includes(type)) {
                        formatAlertType.US.push(type);
                    } else if (EP_FILE_WRAPPER_TYPE.includes(type)) {
                        formatAlertType.EP.push(type);
                    }
                });
                if (!formatAlertType[this.PN] || formatAlertType[this.PN].length === 0) {
                    item.disabled = true;
                }
                item.formatAlertType = formatAlertType;
            });
            return list;
        }
    },
    methods: {
        closePopover() {
            this.$emit('close', false);
        },
        goManage() {
            window.open_noopener(`${HOME_URI}/alert`, '_blank', 'noopener');
        },
        async goCreate() {
            if (this.isLoadingLimit) return;
            this.isLoadingLimit = true;
            const { alert_creation } = await getEmailAlertLimitApi();
            if (alert_creation && alert_creation > 0) {
                this.$emit('close', true);
            } else {
                const alertLink = `${HOME_URI}/alert`;
                Toast({
                    message: this.$t('commonStatusCode.13250130', { link: alertLink }),
                    type: 'warning',
                    showClose: true,
                    dangerouslyUseHTMLString: true
                });
            }
            this.isLoadingLimit = false;
        },
        addPatentIntoAlert(item) {
            if (item.disabled || item.apno.includes(this.patent.APN) || this.isLoadingAddIn) return;
            this.isLoadingAddIn = true;
            createFileWrapperApi({ alert_id: item.alert_id, apno: this.patent.APN, email: item.email })
                .then(() => {
                    this.$emit('addInSuccess', item.alert_id);
                    this.isLoadingAddIn = false;
                })
                .catch(err => {
                    Toast({
                        message: err._error_msg,
                        type: 'warning',
                        showClose: true,
                        dangerouslyUseHTMLString: true
                    });
                    this.isLoadingAddIn = false;
                });
        },
        getTotalCount(item) {
            const total = item.alert_send.reduce((sum, item) => sum += item.count || 1, 0);
            return item.send_to_myself ? (total + 1) : total;
        }
    }
};
</script>

<style lang="scss">
    .file-wrapper-detail {
        width: 430px;
        padding: 4px;
        color: #061632;
        .file-wrapper-detail__container {
            margin-bottom: 12px;
            &:last-of-type {
                margin-bottom: 0;
            }
        }
        .file-wrapper-detail__header {
            margin-bottom: 24px;
            color: #061632;
            font-weight: 500;
            position: relative;
            padding-right: 30px;
            font-size: 14px;
            .file-wrapper-detail__header__close {
                font-size: 24px;
                fill:rgb(80, 95, 121);
                cursor: pointer;
                position: absolute;
                top: 0;
                right: 0;
            }
        }
        .file-wrapper-detail__body {
            height: 340px;
            overflow: auto;
            padding-right: 4px;
            .file-wrapper-detail__info {
                display: flex;
                align-items: center;
                .file-wrapper-detail__status {
                    padding: 0 4px;
                    display: inline-block;
                    background-color: #45A321;
                    color: #fff;
                    font-size: 12px;
                    height: 18px;
                    line-height: 18px;
                    &--inactive {
                        background-color: #7A869A;
                    }
                }
                .file-wrapper-detail__add-button {
                    display: flex;
                    align-items: center;
                    background-color: #F4F5F7;
                    border-radius: 4px;
                    border: 1px solid #B3BAC5;
                    padding: 0 8px 0 4px;
                    cursor: pointer;
                    margin-left: 8px;
                    &--active {
                        background-color: #1976D2 !important;
                        color: #fff;
                        border-color: #1976D2;
                        cursor: default;
                        .patsnap-biz-icon {
                            fill: #fff !important;
                        }
                    }
                    &--disable {
                        color: $text-disable;
                        cursor: not-allowed;
                        .patsnap-biz-icon {
                            fill: $text-disable !important;
                        }
                    }
                    &:hover:not(.file-wrapper-detail__add-button--disable) {
                        background-color: #DFE1E6;
                    }
                    .patsnap-biz-icon {
                        font-size:24px;
                        fill: #505F79;
                        margin-bottom:0
                    }
                }
            }
            .file-wrapper-detail__content {
                background-color: #F4F5F7;
                border-radius: 4px;
                padding: 12px;
                .file-wrapper-detail__content-item {
                    display: flex;
                    margin-bottom: 8px;
                    &:last-of-type {
                        margin-bottom: 0;
                    }
                    .file-wrapper-detail__content-item-title {
                        font-weight: 400;
                        color: #5e6c84;
                        line-height: 24px;
                        width: 88px;
                        flex-shrink: 0;
                        word-break: normal;
                        word-break: break-word;
                        margin-right: 4px;
                    }
                    .file-wrapper-detail__content-item-body {
                        line-height: 24px;
                        flex: 1;
                        .file-wrapper-detail__content-item-alert-type {
                            display: inline-block;
                            height: 24px;
                            line-height: 24px;
                            border-radius: 4px;
                            background-color: #DFE1E6;
                            margin-right: 4px;
                            margin-bottom: 4px;
                            padding: 0 8px;
                        }
                        .file-wrapper-detail__content-item-email {
                            margin-bottom: 4px;
                            &:last-of-type {
                                margin-bottom: 0;
                            }
                        }
                    }
                }
            }
        }
        .file-wrapper-detail__footer {
            display: flex;
            justify-content: space-between;
            padding: 16px 0;
            .patsnap-biz-icon {
                fill: $status-info-primary;
                font-size: 24px;
                margin-bottom: 0;
            }
            .file-wrapper-detail__footer-button {
                display: flex;
                align-items: center;
                height: 32px;
                color: #1976D2;
                border-radius: 4px;
                cursor: pointer;
                padding: 0 12px 0 6px;
                &-text {
                    margin-left: 4px;
                    line-height: 24px;
                }
                &:hover {
                    background-color: #EBECF0;
                }
            }
        }
    }
</style>
